import type { TExample } from '@atlas/uicorn/preview/modules';
import React from 'react';
import styled from 'styled-components';
import { InputNumber as AntInputNumber, InputNumberProps as TAntInputNumberProps } from 'antd';

const NumberInput = React.memo(
    React.forwardRef<TNumberInputRef, TNumberInputProps>(function NumberInput(props, ref): React.ReactElement {
        const { hideControls, ...inputProps } = props;
        return <SNumberInput {...inputProps} controls={hideControls ? false : undefined} ref={ref} />;
    }),
);

export default NumberInput;

// #region Styles

const SNumberInput = styled(AntInputNumber)`
    ${(p) => p.theme.patch.input}

    width: 100%;

    .ant-input-number-handler-wrap {
        width: 2.2rem;
        border-top-right-radius: inherit;
        border-bottom-right-radius: inherit;
        background: inherit;
    }

    .ant-input-number-handler-up,
    .ant-input-number-handler-down {
        border-bottom-right-radius: inherit;
        border-left: 0.1rem solid ${(p) => p.theme.color.grey6};
    }

    &.ant-input-number:hover:not(.ant-input-number-borderless) .ant-input-number-handler-down,
    &.ant-input-number-focused:not(.ant-input-number-borderless) .ant-input-number-handler-down {
        border-top: 0.1rem solid ${(p) => p.theme.color.grey6};
    }

    .ant-input-number-handler-wrap .ant-input-number-handler .ant-input-number-handler-up-inner,
    .ant-input-number-handler-wrap .ant-input-number-handler .ant-input-number-handler-down-inner {
        font-size: 0.7rem;
    }

    .ant-input-number-handler:hover .ant-input-number-handler-up-inner,
    .ant-input-number-handler:hover .ant-input-number-handler-down-inner {
        color: ${(p) => p.theme.color.primary};
    }

    .ant-input-number-handler-up-inner {
        margin-top: -0.5rem;
    }

    .ant-input-number-handler-up-inner,
    .ant-input-number-handler-down-inner {
        right: 0.4rem;
        width: 1.2rem;
        height: 1.2rem;
        color: ${(p) => p.theme.color.grey7};
        line-height: 1.2rem;
    }

    .ant-input-number-input-wrap {
        line-height: 0;
    }

    .ant-input-number-input {
        height: 2.2rem;
        padding: 0 2.2rem 0 0;
    }
`;

// #endregion

// #region Types

export type TNumberInputProps = Pick<
    TAntInputNumberProps,
    | 'className'
    | 'style'
    | 'value'
    | 'defaultValue'
    | 'onChange'
    | 'onKeyDown'
    | 'placeholder'
    | 'disabled'
    | 'autoFocus'
    | 'min'
    | 'max'
    | 'required'
    | 'precision'
    | 'step'
> & { hideControls?: boolean };

export type TNumberInputRef = HTMLInputElement;

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return <NumberInput disabled={props.disabled} onChange={props.onChange} />;
    },
    controls: {
        disabled: 'boolean',
    },
    handlers: {
        onChange: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const { describe, it, expect } = import.meta.vitest;
    describe('NumberInput', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });
    });
}

// #endregion
